import React, { useEffect, useRef, useState } from 'react';
import { Eye, EyeOff, Copy, Check } from 'lucide-react';
import { useToast } from '../../context/ToastContext';

interface SensitiveFieldProps {
  label: string;
  maskedPreview: string;
  hasValue: boolean;
  fetchValue: () => Promise<string | null>;
  onLockedError?: () => void;
}

const AUTO_REMASK_MS = 20000;

const SensitiveField: React.FC<SensitiveFieldProps> = ({ label, maskedPreview, hasValue, fetchValue, onLockedError }) => {
  const { toast } = useToast();
  const [revealedValue, setRevealedValue] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => () => { if (timerRef.current) clearTimeout(timerRef.current); }, []);

  const handleReveal = async () => {
    if (revealedValue !== null) {
      setRevealedValue(null);
      if (timerRef.current) clearTimeout(timerRef.current);
      return;
    }
    setLoading(true);
    try {
      const value = await fetchValue();
      setRevealedValue(value ?? '');
      timerRef.current = setTimeout(() => setRevealedValue(null), AUTO_REMASK_MS);
    } catch (e: any) {
      if (e?.response?.status === 401 && onLockedError) {
        onLockedError();
      } else {
        toast('Unable to reveal this value right now', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = async () => {
    if (revealedValue === null) return;
    await navigator.clipboard.writeText(revealedValue);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  if (!hasValue) {
    return (
      <div style={rowStyle}>
        <span style={labelStyle}>{label}</span>
        <span style={{ ...valueStyle, color: 'var(--text-muted)' }}>Not set</span>
      </div>
    );
  }

  return (
    <div style={rowStyle}>
      <span style={labelStyle}>{label}</span>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <span style={{ ...valueStyle, fontFamily: 'monospace' }}>
          {revealedValue !== null ? revealedValue : maskedPreview}
        </span>
        <button onClick={handleReveal} disabled={loading} style={iconButtonStyle} title={revealedValue !== null ? 'Hide' : 'Reveal'}>
          {revealedValue !== null ? <EyeOff size={14} /> : <Eye size={14} />}
        </button>
        {revealedValue !== null && (
          <button onClick={handleCopy} style={iconButtonStyle} title="Copy">
            {copied ? <Check size={14} /> : <Copy size={14} />}
          </button>
        )}
      </div>
    </div>
  );
};

const rowStyle: React.CSSProperties = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '10px 0', borderBottom: '1px solid var(--border)' };
const labelStyle: React.CSSProperties = { fontSize: 12, color: 'var(--text-secondary)', fontWeight: 500 };
const valueStyle: React.CSSProperties = { fontSize: 14, color: 'var(--text-primary)' };
const iconButtonStyle: React.CSSProperties = { background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 6, padding: 5, cursor: 'pointer', color: 'var(--text-secondary)', display: 'flex', alignItems: 'center' };

export default SensitiveField;
